// Misconception Detection — identifies systematic errors from distractor patterns
// Multi-cert: all misconception data is cert-scoped via certPath()

import { certPath, read, write } from './firebase-store.js';
import { isGraphReady, getConceptsForSkill, getSkillsForConcept } from './knowledge-graph.js';

/**
 * Record a distractor selection for misconception tracking (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {string} questionId
 * @param {string} selectedChoice - the wrong choice the user picked
 * @param {Array<string>} skills
 * @param {Array<string>} conceptTags
 */
export async function recordDistractor(userId, certId, questionId, selectedChoice, skills, conceptTags) {
  if (!selectedChoice) return;

  let misconceptions;
  try {
    misconceptions = await read(certPath(userId, certId, 'misconceptions')) || {};
  } catch (e) {
    misconceptions = {};
  }

  // Track per-question distractor frequency
  if (!misconceptions.distractors) misconceptions.distractors = {};
  const key = questionId + ':' + selectedChoice;
  misconceptions.distractors[key] = (misconceptions.distractors[key] || 0) + 1;

  // Track per-skill error count
  if (!misconceptions.skillErrors) misconceptions.skillErrors = {};
  for (const skill of (skills || [])) {
    if (!misconceptions.skillErrors[skill]) misconceptions.skillErrors[skill] = { count: 0, lastSeen: null };
    misconceptions.skillErrors[skill].count++;
    misconceptions.skillErrors[skill].lastSeen = new Date().toISOString();
  }

  // Track per-concept error count
  if (!misconceptions.conceptErrors) misconceptions.conceptErrors = {};
  for (const concept of (conceptTags || [])) {
    if (!misconceptions.conceptErrors[concept]) misconceptions.conceptErrors[concept] = { count: 0, lastSeen: null };
    misconceptions.conceptErrors[concept].count++;
    misconceptions.conceptErrors[concept].lastSeen = new Date().toISOString();
  }

  // Update total error count
  misconceptions.totalErrors = (misconceptions.totalErrors || 0) + 1;
  misconceptions.lastUpdated = new Date().toISOString();

  try {
    await write(certPath(userId, certId, 'misconceptions'), misconceptions);
  } catch (e) { /* Firebase not available */ }
}

/**
 * Detect misconception patterns (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {object} skillMastery - current BKT mastery data
 * @returns {Promise<Array>}
 */
export async function detectMisconceptions(userId, certId, skillMastery) {
  let misconceptions;
  try {
    misconceptions = await read(certPath(userId, certId, 'misconceptions'));
  } catch (e) { return []; }
  if (!misconceptions) return [];

  const detected = [];
  const mastery = skillMastery || {};

  // Skill-level misconceptions
  const skillErrors = misconceptions.skillErrors || {};
  for (const [skill, data] of Object.entries(skillErrors)) {
    if (data.count < 3) continue;

    const skillMasteryLevel = mastery[skill]?.pL || 0;

    if (skillMasteryLevel < 0.5) {
      const severity = data.count >= 7 ? 'high' : data.count >= 5 ? 'medium' : 'low';

      let relatedConcepts = [];
      if (isGraphReady(certId)) {
        relatedConcepts = getConceptsForSkill(certId, skill);
      }

      detected.push({
        type: 'skill',
        name: skill,
        errorCount: data.count,
        mastery: skillMasteryLevel,
        severity,
        lastSeen: data.lastSeen,
        relatedConcepts
      });
    }
  }

  // Concept-level misconceptions
  const conceptErrors = misconceptions.conceptErrors || {};
  for (const [concept, data] of Object.entries(conceptErrors)) {
    if (data.count < 3) continue;

    let relatedSkillsWeak = false;
    if (isGraphReady(certId)) {
      const skills = getSkillsForConcept(certId, concept);
      const weakSkills = skills.filter(s => (mastery[s]?.pL || 0) < 0.5);
      relatedSkillsWeak = weakSkills.length > skills.length * 0.5;
    }

    if (relatedSkillsWeak || data.count >= 5) {
      const severity = data.count >= 7 ? 'high' : data.count >= 5 ? 'medium' : 'low';

      detected.push({
        type: 'concept',
        name: concept,
        errorCount: data.count,
        severity,
        lastSeen: data.lastSeen
      });
    }
  }

  const severityOrder = { high: 3, medium: 2, low: 1 };
  return detected.sort((a, b) =>
    (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0) ||
    b.errorCount - a.errorCount
  );
}

/**
 * Get the top distractor choices for a specific question (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {string} questionId
 * @returns {Promise<Array<{choice, count}>>}
 */
export async function getTopDistractors(userId, certId, questionId) {
  let misconceptions;
  try {
    misconceptions = await read(certPath(userId, certId, 'misconceptions'));
  } catch (e) { return []; }
  if (!misconceptions || !misconceptions.distractors) return [];

  const prefix = questionId + ':';
  const results = [];

  for (const [key, count] of Object.entries(misconceptions.distractors)) {
    if (key.startsWith(prefix)) {
      results.push({
        choice: key.slice(prefix.length),
        count
      });
    }
  }

  return results.sort((a, b) => b.count - a.count);
}

/**
 * Get misconception summary for the learner profile (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {object} skillMastery
 * @returns {Promise<{totalMisconceptions, topMisconceptions, errorClusters}>}
 */
export async function getMisconceptionSummary(userId, certId, skillMastery) {
  const detected = await detectMisconceptions(userId, certId, skillMastery);

  const highSeverity = detected.filter(m => m.severity === 'high');
  const errorClusters = {};

  for (const m of detected) {
    if (m.type === 'concept') {
      errorClusters[m.name] = {
        errorCount: m.errorCount,
        severity: m.severity
      };
    }
  }

  return {
    totalMisconceptions: detected.length,
    topMisconceptions: detected.slice(0, 5).map(m => ({
      type: m.type,
      name: m.name,
      errorCount: m.errorCount,
      severity: m.severity
    })),
    highSeverityCount: highSeverity.length,
    errorClusters
  };
}
